import { z } from 'zod';
/**
 * OrgPinnedEntity — an org-scoped pinned entity. Distinct from Bookmark,
 * which is per-user: every member of the org sees every OrgPinnedEntity
 * on their Dossiers page without each having to bookmark the same thing.
 *
 * Seeded by the onboarding pipeline with one row for the company itself
 * (the org's own "company-dossier") so every teammate who joins later
 * sees the company in their dossiers without an explicit bookmark step
 * at their own onboarding.
 *
 * The Dossiers page reads `[...personalBookmarks, ...orgPinnedEntities]`
 * and dedupes by `refId` — a user bookmarking an org-pinned entity
 * doesn't double-list it. Stable doc id: `${orgId}_${entityId}` keeps
 * the upsert idempotent without a query.
 */
export const ORG_PINNED_ENTITIES_COLLECTION = 'orgPinnedEntities';
export const OrgPinnedEntityType = z.enum([
    'company',
    'customer',
    'person',
    'topic',
    'product',
    'entity',
]);
export const OrgPinnedEntity = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /** Stable id of the entity being pinned. For the seeded company-dossier
     *  this is `company-${orgId}`. For future custom-pinned entities,
     *  whatever the graph layer assigns. */
    entityId: z.string().min(1),
    type: OrgPinnedEntityType,
    name: z.string().min(1),
    /** Optional one-line caption ("Series-A LeanOpx HQ · 12 people"). */
    sub: z.string().optional(),
    /** Optional URL — for company-type rows, the homepage. */
    url: z.string().url().optional(),
    /** Free-form description — for company-type rows, the
     *  BusinessContext.companyDescription. */
    description: z.string().optional(),
    pinnedAt: z.string(),
    pinnedByUid: z.string().min(1),
    /** Why it got pinned — `onboarding-seed` marks the company seed so we
     *  can surface "from your onboarding" provenance later. */
    source: z.enum(['onboarding-seed', 'manual']).default('manual'),
});
/** Stable doc id for the (orgId, entityId) pair. */
export function orgPinnedEntityDocId(orgId, entityId) {
    return `${orgId}_${entityId}`;
}
/** Wire shape returned by `GET /api/orgs/:o/pinned-entities`. */
export const ListOrgPinnedEntitiesResponse = z.object({
    entities: z.array(OrgPinnedEntity),
});
